import { useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { Field, Input, Label } from "@headlessui/react";
import { localeShortDateTime } from "../../../app/datetimeUtils";
import { handleEnterKey } from "../../../app/keyboardUtils";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import Select from "../../../components/input/Select";
import {
	useGetSchoolCleverSettingsQuery,
	useGetSchoolClassLinkSettingsQuery,
	SchoolCleverSettingsViewModel,
	SchoolClassLinkSettingsViewModel,
	useUpdateSchoolCleverSettingsMutation,
	useUpdateSchoolClassLinkSettingsMutation,
} from "../schoolsApiSlice";

interface SchoolOutideSiteSettingsFormProps {
	schoolId: number;
	isCleverForm: boolean;
	onUpdateOutsideSiteId: (id: string) => void;
	onClose: () => void;
}

export default function SchoolOutsideSiteSettingsForm({
	schoolId,
	isCleverForm,
	onUpdateOutsideSiteId: updateOutsideSiteId,
	onClose,
}: SchoolOutideSiteSettingsFormProps) {
	const [outsideSiteId, setOutsideSiteId] = useState("");
	const [onboarding, setOnboarding] = useState(false);

	const [errorMessage, setErrorMessage] = useState("");

	const cleverSettingsResult = useGetSchoolCleverSettingsQuery(
		isCleverForm ? schoolId : skipToken
	);
	const classLinkSettingsResult = useGetSchoolClassLinkSettingsQuery(
		!isCleverForm ? schoolId : skipToken
	);

	const [callUpdateCleverSettings] = useUpdateSchoolCleverSettingsMutation();
	const [callUpdateClassLinkSettings] = useUpdateSchoolClassLinkSettingsMutation();

	const settings = isCleverForm ? cleverSettingsResult.data : classLinkSettingsResult.data;

	useEffect(() => {
		if (!settings) return;
		const outsideSiteId = isCleverForm
			? (settings as SchoolCleverSettingsViewModel).cleverId
			: (settings as SchoolClassLinkSettingsViewModel).classLinkId;
		setOutsideSiteId(outsideSiteId ?? "");
		setOnboarding(settings.isOnboarding);
	}, [settings, isCleverForm]);

	function handleSave() {
		const mutation = isCleverForm
			? callUpdateCleverSettings({
					...(settings as SchoolCleverSettingsViewModel),
					cleverId: outsideSiteId,
					isOnboarding: onboarding,
				})
			: callUpdateClassLinkSettings({
					...(settings as SchoolClassLinkSettingsViewModel),
					classLinkId: outsideSiteId,
				});

		mutation
			.unwrap()
			.then(() => {
				updateOutsideSiteId(outsideSiteId);
				onClose();
			})
			.catch((error: any) => {
				setErrorMessage(error.data);
			});
	}

	function handleCancel() {
		onClose();
	}

	return (
		<div>
			{errorMessage && (
				<AlertMessage
					className="text-sm mt-4 py-2 !px-4"
					type={AlertLevel.Error}
					message={errorMessage}
					showIcon={true}
				/>
			)}
			<div className="mt-3 grid grid-cols-1 gap-y-3">
				<Field className="col-span-full">
					<Label htmlFor="outsideSiteId" className="font-medium text-sm">
						{isCleverForm ? "Clever ID" : "ClassLink ID"}
					</Label>
					<Input
						id="outsideSiteId"
						name="outsideSiteId"
						type="text"
						className={
							"block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						}
						value={outsideSiteId}
						onChange={(e) => setOutsideSiteId(e.target.value)}
					/>
				</Field>
				{isCleverForm && (
					<Select
						value={onboarding ? "true" : "false"}
						setValue={(value) => setOnboarding(value === "true")}
						simpleListBoxId="onboarding"
						options={[
							{ value: "true", label: "Waiting" },
							{ value: "false", label: "Complete" },
						]}
						label="Onboarding Status"
						fieldClasses="col-span-full"
					/>
				)}
				<Field className="col-span-full">
					<Label htmlFor="lastSyncAt" className="font-medium text-sm">
						Last Sync At
					</Label>
					<Input
						id="lastSyncAt"
						name="lastSyncAt"
						type="text"
						readOnly
						className={
							"block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						}
						value={
							settings?.lastSyncAt == undefined
								? ""
								: localeShortDateTime(settings.lastSyncAt, true)
						}
					/>
				</Field>
			</div>
			<div className="mt-4 grid grid-cols-2 gap-3">
				<button
					type="button"
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</button>
				<button
					type="button"
					id="confirm-button"
					onClick={handleSave}
					onKeyDown={(e) => handleEnterKey(e, handleSave)}
					className="btn btn-primary">
					Save
				</button>
			</div>
		</div>
	);
}
